import type { Asset, AssetPhoto } from '@/types/asset';

/**
 * Get the primary photo URL for an asset.
 * For list views, uses the lightweight primaryImageUrl field (when photos array is empty).
 * For detail views, returns the URL of the primary photo from the photos array,
 * or the first photo if no primary is set, or undefined if no photos exist.
 */
export function getAssetPrimaryPhotoUrl(asset: Pick<Asset, 'photos' | 'primaryImageUrl'>): string | undefined {
  // First check if we have a primaryImageUrl (used in list views where photos array is empty)
  if (asset.primaryImageUrl) {
    return asset.primaryImageUrl;
  }
  
  // Fall back to photos array (used in detail views where full photo data is loaded)
  if (!asset.photos || asset.photos.length === 0) {
    return undefined;
  }
  const primaryPhoto = asset.photos.find(p => p.isPrimary);
  return primaryPhoto?.url ?? asset.photos[0]?.url;
}

/**
 * Get the primary photo object for an asset.
 * Returns the primary photo, or the first photo if no primary is set,
 * or undefined if no photos exist.
 */
export function getAssetPrimaryPhoto(asset: Pick<Asset, 'photos'>): AssetPhoto | undefined {
  if (!asset.photos || asset.photos.length === 0) {
    return undefined;
  }
  return asset.photos.find(p => p.isPrimary) ?? asset.photos[0];
}
